import { ScrollArea, Table, Text } from '@mantine/core';
import cx from 'clsx';
import { FC, useCallback } from 'react';

import styles from './styles.module.css';
import { Th } from './Th';
import { useSortTable } from './use-sort-table';

export type Sort = {
  content: JSX.Element;
  rows: Record<string, string>;
  onRowClick?: () => void;
};

type Column = {
  key: string;
  label: string;
};

type Props = {
  data: Sort[];
  columns: Column[];
  isLoading: boolean;
};

export const DataTable: FC<Props> = ({ columns, data }) => {
  const { sort, sortBy, isReversed, sortedData } = useSortTable(data);

  const handleSort = useCallback(
    (key: string) => {
      sort(key);
    },
    [sort]
  );

  const heads = (
    <Table.Tr className={styles.head}>
      {columns.map(({ key, label }) => (
        <Th
          key={key}
          sorted={sortBy === key}
          reversed={isReversed}
          onSort={() => handleSort(key)}
        >
          {label}
        </Th>
      ))}
    </Table.Tr>
  );

  const rows = sortedData.map((data, index) => {
    return (
      <Table.Tr
        className={cx(styles.row, data?.onRowClick && styles.clickableRow)}
        key={index}
        onClick={data?.onRowClick}
      >
        {data.content}
      </Table.Tr>
    );
  });

  const noContent = (
    <Table.Tr className={styles.row}>
      <Table.Td colSpan={columns?.length}>
        <Text fw={500} ta="center">
          Nothing found
        </Text>
      </Table.Td>
    </Table.Tr>
  );

  return (
    <ScrollArea>
      <Table
        className={styles.table}
        withRowBorders={false}
        horizontalSpacing="lg"
        verticalSpacing="lg"
      >
        <Table.Thead>{heads}</Table.Thead>
        <Table.Tbody>{rows?.length > 0 ? rows : noContent}</Table.Tbody>
      </Table>
    </ScrollArea>
  );
};
